import { useMemo } from 'react';

import CheckCircleIcon from '@/assets/icons/check-circle.svg';
import ExclamationIcon from '@/assets/icons/exclamation.svg';
import ExclamationCircleIcon from '@/assets/icons/exclamation-circle.svg';
import { ChatAltIcon } from '@/components/icons/chat-alt';
import { CloseIcon } from '@/components/icons/close';
import { cn } from '@/utils/classnames';

import { Button } from './button';
import { Typography } from './typography';

interface BannerProps {
  color: 'neutral' | 'rose' | 'green' | 'amber' | 'brand' | 'blue' | 'white';
  icon?: React.ReactNode;
  title?: string;
  description: React.ReactNode;
  button?: React.ReactNode;
  className?: string;
  borderLess?: boolean;
  /** Called when the user clicks the dismiss (close) control. Omit to hide the close button. */
  onClose?: () => void;
}

export function Banner({
  color,
  icon,
  title,
  description,
  button,
  className,
  borderLess = false,
  onClose,
}: BannerProps) {
  const { colorClass, iconNode } = useMemo(() => {
    switch (color) {
      case 'neutral':
        return {
          colorClass: 'bg-fx-neutral-100 border-fx-neutral-300',
          iconNode: <ChatAltIcon className="text-fx-neutral-400" />,
        };
      case 'rose':
        return {
          colorClass: 'bg-fx-rose-50 border-fx-rose',
          iconNode: <img src={ExclamationIcon} alt="Exclamation" />,
        };
      case 'green':
        return {
          colorClass: 'bg-fx-green-50 border-fx-green',
          iconNode: <img src={CheckCircleIcon} alt="Check Circle" />,
        };
      case 'amber':
        return {
          colorClass: 'bg-fx-amber-50 border-fx-amber',
          iconNode: (
            <img src={ExclamationCircleIcon} alt="Exclamation Circle" />
          ),
        };
      case 'brand':
        return {
          colorClass: 'bg-fx-brand-50 border-fx-brand',
          iconNode: <ChatAltIcon className="text-fx-brand" />,
        };
      case 'blue':
        return {
          colorClass: 'bg-fx-blue-50 border-fx-blue',
          iconNode: <ChatAltIcon className="text-fx-blue" />,
        };
      case 'white':
        return {
          colorClass: 'border-fx-neutral-300 bg-white',
          iconNode: <ChatAltIcon className="text-fx-neutral-400" />,
        };
      default:
        return {
          colorClass: 'text-fx-neutral-400',
          iconNode: <ChatAltIcon className="text-fx-neutral-400" />,
        };
    }
  }, [color]);

  return (
    <div
      className={cn(
        'flex items-center gap-2 self-stretch rounded-md p-2',
        !borderLess && 'border shadow-sm',
        colorClass,
        className,
      )}
    >
      {icon ?? iconNode}
      <div className="flex grow flex-col items-start justify-center">
        {title && <Typography variant="body2">{title}</Typography>}
        {typeof description === 'string' ? (
          <Typography variant="body1">{description}</Typography>
        ) : (
          description
        )}
      </div>
      {button}
      {onClose ? (
        <Button
          variant="icon"
          size="icon"
          className="flex h-6 w-6 items-center justify-center"
          onClick={onClose}
        >
          <CloseIcon className="text-fx-neutral-600" />
        </Button>
      ) : null}
    </div>
  );
}
